import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import { callNumber } from './shared/rcWidget';
import TimelineDrawer from './shared/TimelineDrawer';
import ClockButton from './shared/ClockButton';
import useTextCheck from './shared/useTextCheck';
import TextCheckNotice from './shared/TextCheckNotice';
import './MessagesInbox.css';

const NUDGE_REASONS = [
  "Hasn't responded in a while",
  "Requested a callback",
  "Needs help finishing background check",
  "Confused about pay or schedule",
  "Other (custom reason)",
];
const CUSTOM_REASON = "Other (custom reason)";

const TRIGGER_META = {
  intro_text: { label: 'Rated 5+', cls: 'rated' },
  fadv_sent: { label: 'FADV Sent', cls: 'fadv' },
  drug_physical: { label: 'Drug Test', cls: 'drug' },
};

const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

// "Missed call · 3h ago · voicemail" for unreturned missed calls (October 2026)
function missedLabel(c) {
  const h = Math.max(0, Math.round((Date.now() - new Date(c.last_missed)) / 3600000));
  const ago = h < 1 ? 'just now' : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
  return `Missed ${c.missed_count > 1 ? `${c.missed_count} calls` : 'call'} · ${ago}${c.voicemail || c.missed_voicemail ? ' · voicemail' : ''}`;
}

function formatClock(ts) {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function dayLabel(ts) {
  const d = new Date(ts);
  const now = new Date();
  const startOf = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function nudgeLabel(state) {
  if (!state?.nudge_reason) return null;
  return state.nudge_reason === CUSTOM_REASON ? (state.nudge_custom || 'Custom reason') : state.nudge_reason;
}

export default function MessagesInbox({ user, onLogout }) {
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const [pageTab, setPageTab] = useState('messages'); // 'messages' | 'calls'
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all'); // 'all' | 'prior' | 'new'
  const [companyFilter, setCompanyFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [activeCandidateId, setActiveCandidateId] = useState(null);
  const [thread, setThread] = useState(null); // { candidate, messages, state, fromNumber, missed }
  const [timelineId, setTimelineId] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [clarifyQ, setClarifyQ] = useState(null); // text checker: send held, recruiter asked to clarify
  const { unclear: draftUnclear } = useTextCheck(replyText);
  const [sending, setSending] = useState(false);

  const [nudgePanelOpen, setNudgePanelOpen] = useState(false);
  const [nudgeReason, setNudgeReason] = useState(NUDGE_REASONS[0]);
  const [nudgeCustom, setNudgeCustom] = useState('');

  const [picker, setPicker] = useState(null); // null | { mode: 'compose' } | { mode: 'attach', phoneNumber, recruiterId }
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerResults, setPickerResults] = useState([]);

  const messagesEndRef = useRef(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/conversations`, authHeaders());
      setConversations(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnmatched = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/unmatched`, authHeaders());
      setUnmatched(data);
    } catch (err) { /* non-critical, don't block the page over this */ }
  }, []);

  useEffect(() => { loadConversations(); loadUnmatched(); }, [loadConversations, loadUnmatched]);

  // Calls tab loads lazily -- only fetched the first time a recruiter actually clicks it, not on
  // page load, since it's a live RC API call sitting behind a 45s server-side cache (see
  // routes/sms-inbox.js) and there's no reason to spend that budget on recruiters who never open it.
  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/calls`, authHeaders());
      setCalls(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load call log');
    } finally {
      setCallsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pageTab === 'calls') loadCalls();
  }, [pageTab, loadCalls]);

  // ?candidateId=X (from the quick-text pop-up's "Open in Messages") opens that thread on load
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('candidateId');
    if (fromUrl) openThread(parseInt(fromUrl, 10));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calls from Messages are logged (call_started) so the missed-call marker clears right away
  const callCandidate = async (candidate) => {
    callNumber(candidate.phone);
    try {
      await axios.post(`${API_URL}/api/candidates/${candidate.id}/call-started`, {}, authHeaders());
      loadConversations();
      openThread(candidate.id);
    } catch (e) { /* the call itself already started */ }
  };

  const openThread = async (candidateId) => {
    setActiveCandidateId(candidateId);
    setNudgePanelOpen(false);
    setThreadLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/thread/${candidateId}`, authHeaders());
      setThread(data);
      if (data.state.unread) {
        await axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/read`, { unread: false }, authHeaders());
        setConversations(prev => prev.map(c => c.candidate_id === candidateId ? { ...c, unread: false } : c));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load conversation');
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
  }, [thread]);

  // Silent refresh of the currently-open thread -- called by the poll below, not by clicking
  // a row (openThread does that, and always marks read). If a new inbound text arrived while
  // the recruiter was already looking at this thread, immediately mark it read again rather
  // than leaving it flagged unread under their nose.
  const pollActiveThread = useCallback(async (candidateId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-inbox/thread/${candidateId}`, authHeaders());
      setThread(data);
      if (data.state.unread) {
        axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/read`, { unread: false }, authHeaders()).catch(() => {});
        setThread(t => (t ? { ...t, state: { ...t.state, unread: false } } : t));
        setConversations(prev => prev.map(c => c.candidate_id === candidateId ? { ...c, unread: false } : c));
      }
    } catch (err) { /* silent -- a missed poll tick isn't worth surfacing an error toast for */ }
  }, []);

  // Live-updates the page every 15s: refreshes the sidebar (new conversations, previews, unread
  // dots) and, if a thread is open, that thread's messages -- so a reply shows up without the
  // recruiter having to leave and come back. Matches the polling interval decision (not
  // WebSockets) discussed for this feature -- see CLAUDE.md, Messages Inbox section.
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
      loadUnmatched();
      if (activeCandidateId) pollActiveThread(activeCandidateId);
    }, 15000);
    return () => clearInterval(interval);
  }, [activeCandidateId, loadConversations, loadUnmatched, pollActiveThread]);

  const companies = [...new Map(conversations.filter(c => c.company_id).map(c => [c.company_id, c.company_name])).entries()];

  const filteredConversations = conversations
    .filter(c => companyFilter === 'all' || String(c.company_id) === String(companyFilter))
    .filter(c => statusFilter !== 'unread' || c.unread)
    .filter(c => statusFilter !== 'nudges' || c.nudge_reason)
    .filter(c => originFilter === 'all' || c.source_type === originFilter)
    .filter(c => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return c.candidate_name?.toLowerCase().includes(q) || c.recruiter_name?.toLowerCase().includes(q);
    })
    .sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));

  const counts = {
    all: conversations.length,
    unread: conversations.filter(c => c.unread).length,
    nudges: conversations.filter(c => c.nudge_reason).length,
  };

  const togglePriority = async (candidateId, current, ev) => {
    ev?.stopPropagation();
    setConversations(prev => prev.map(c => c.candidate_id === candidateId ? { ...c, priority: !current } : c));
    if (thread?.candidate?.id === candidateId) setThread(t => ({ ...t, state: { ...t.state, priority: !current } }));
    try {
      await axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/priority`, { priority: !current }, authHeaders());
    } catch (err) { loadConversations(); }
  };

  const toggleUnread = async (candidateId, current, ev) => {
    ev?.stopPropagation();
    setConversations(prev => prev.map(c => c.candidate_id === candidateId ? { ...c, unread: !current } : c));
    if (thread?.candidate?.id === candidateId) setThread(t => ({ ...t, state: { ...t.state, unread: !current } }));
    try {
      await axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/read`, { unread: !current }, authHeaders());
    } catch (err) { loadConversations(); }
  };

  const openNudgePanel = () => {
    const current = thread?.state;
    setNudgeReason(current?.nudge_reason || NUDGE_REASONS[0]);
    setNudgeCustom(current?.nudge_reason === CUSTOM_REASON ? (current.nudge_custom || '') : '');
    setNudgePanelOpen(v => !v);
  };

  const saveNudge = async () => {
    const candidateId = thread.candidate.id;
    try {
      await axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/nudge`,
        { reason: nudgeReason, custom: nudgeReason === CUSTOM_REASON ? nudgeCustom.trim() : null }, authHeaders());
      setThread(t => ({ ...t, state: { ...t.state, nudge_reason: nudgeReason, nudge_custom: nudgeReason === CUSTOM_REASON ? nudgeCustom.trim() : null } }));
      setNudgePanelOpen(false);
      loadConversations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save nudge');
    }
  };

  const clearNudge = async () => {
    const candidateId = thread.candidate.id;
    try {
      await axios.patch(`${API_URL}/api/sms-inbox/thread/${candidateId}/nudge`, { reason: null, custom: null }, authHeaders());
      setThread(t => ({ ...t, state: { ...t.state, nudge_reason: null, nudge_custom: null } }));
      setNudgePanelOpen(false);
      loadConversations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear nudge');
    }
  };

  // The server rewrites the text into natural English before sending (utils/textCheck.js) and
  // returns what actually went out; an unclear text comes back 409 and is not sent.
  const sendReply = async (sendAsWritten = false) => {
    if (!replyText.trim() || !thread) return;
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/sms-inbox/thread/${thread.candidate.id}/send`, { message: replyText.trim(), sendAsWritten }, authHeaders());
      setThread(t => ({
        ...t,
        messages: [...t.messages, { id: `local-${Date.now()}`, direction: 'outbound', body: data.sent || replyText.trim(), original_body: data.corrected ? data.original : null, auto_sent: false, trigger_type: null, created_at: new Date().toISOString() }],
        fromNumber: data.fromNumber || t.fromNumber,
      }));
      setReplyText('');
      setClarifyQ(null);
      loadConversations();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.code === 'needs_clarification') setClarifyQ(err.response.data.question);
      else setError(err.response?.data?.error || 'Failed to send text');
    } finally {
      setSending(false);
    }
  };

  // ---- New Message / Attach-unmatched candidate picker (shared) ----

  useEffect(() => {
    if (!picker) { setPickerResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/sms-inbox/candidates/search`, { ...authHeaders(), params: { q: pickerQuery } });
        setPickerResults(data);
      } catch (err) { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [picker, pickerQuery]);

  const choosePickerCandidate = async (candidate) => {
    if (picker?.mode === 'attach') {
      try {
        await axios.post(`${API_URL}/api/sms-inbox/unmatched/attach`,
          { phoneNumber: picker.phoneNumber, recruiterId: picker.recruiterId, candidateId: candidate.id }, authHeaders());
        setPicker(null);
        loadUnmatched();
        loadConversations();
        openThread(candidate.id);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to attach message');
      }
    } else {
      setPicker(null);
      openThread(candidate.id);
    }
  };

  const ignoreUnmatched = async (u) => {
    try {
      await axios.post(`${API_URL}/api/sms-inbox/unmatched/ignore`, { phoneNumber: u.phone_number, recruiterId: u.recruiter_id }, authHeaders());
      loadUnmatched();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to ignore');
    }
  };

  let lastDay = null;

  return (
    <div className="mi-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="mi-page-tabs">
        <div className={`mi-page-tab${pageTab === 'messages' ? ' active' : ''}`} onClick={() => setPageTab('messages')}>Messages</div>
        <div className={`mi-page-tab${pageTab === 'calls' ? ' active' : ''}`} onClick={() => setPageTab('calls')}>Calls</div>
      </div>
      {pageTab === 'calls' ? (
        <div className="mi-calls-panel">
          {callsLoading && <div className="mi-empty-list">Loading call log…</div>}
          {!callsLoading && calls.length === 0 && (
            <div className="mi-empty-list">
              No calls in the last 48 hours. If you've been calling candidates and don't see them here,
              make sure RingCentral is connected in User Management.
            </div>
          )}
          {!callsLoading && calls.length > 0 && (
            <table className="mi-calls-table">
              <thead>
                <tr><th>Candidate</th><th>Company</th><th>Direction</th><th>Result</th><th>Duration</th><th>Time</th></tr>
              </thead>
              <tbody>
                {calls.map((call, i) => (
                  <tr key={i} className={call.candidate_id ? '' : 'mi-calls-unmatched-row'}>
                    <td>
                      {call.candidate_id ? (
                        <>
                          <div className="mi-cand-name">{call.candidate_name}</div>
                          <div className="mi-cand-sub">{call.phoneNumber}</div>
                        </>
                      ) : (
                        <>
                          <span className="mi-unmatched-pill">Unmatched</span>
                          <div className="mi-cand-sub">{call.phoneNumber}</div>
                        </>
                      )}
                    </td>
                    <td>{call.company_name || '—'}</td>
                    <td>
                      <span className={`mi-call-dir ${call.direction === 'Inbound' ? 'in' : 'out'}`}>
                        {call.direction === 'Inbound' ? '↙ Inbound' : '↗ Outbound'}
                      </span>
                    </td>
                    <td className={call.result === 'Missed' || call.result === 'No Answer' ? 'mi-call-result-bad' : 'mi-call-result-ok'}>
                      {call.result}
                    </td>
                    <td>{call.duration ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` : '—'}</td>
                    <td>{new Date(call.startTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      <div className="mi-shell">
        <div className="mi-sidebar">
          <div className="mi-sidebar-head">
            <div className="mi-sidebar-head-row">
              <h2>Conversations <span className="mi-count">{filteredConversations.length}</span></h2>
              <button className="mi-compose-btn" onClick={() => { setPicker({ mode: 'compose' }); setPickerQuery(''); }}>+ New</button>
            </div>
            <div className="mi-search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input type="text" placeholder="Search candidates or recruiters..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="mi-filter-row">
            <select className="mi-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All ({counts.all})</option>
              <option value="unread">Unread ({counts.unread})</option>
              <option value="nudges">Nudges ({counts.nudges})</option>
            </select>
            <select className="mi-filter-select" value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
              <option value="all">Prior + New</option>
              <option value="prior">Prior only</option>
              <option value="new">New only</option>
            </select>
          </div>

          {companies.length > 0 && (
            <div className="mi-company-filter-row">
              <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
                <option value="all">All Companies ({conversations.length})</option>
                {companies.map(([id, name]) => (
                  <option key={id} value={id}>{name} ({conversations.filter(c => c.company_id === id).length})</option>
                ))}
              </select>
            </div>
          )}

          <div className="mi-thread-list">
            {loading && <div className="mi-date-sep">Loading…</div>}
            {!loading && filteredConversations.length === 0 && unmatched.length === 0 && (
              <div className="mi-empty-list">No conversations yet. Use "+ New" to text a candidate.</div>
            )}
            {filteredConversations.map(c => {
              const trig = TRIGGER_META[c.trigger_type];
              return (
                <div key={c.candidate_id} className={`mi-thread-item${activeCandidateId === c.candidate_id ? ' active' : ''}${c.unread ? ' unread' : ''}`} onClick={() => openThread(c.candidate_id)}>
                  <button className={`mi-star-btn${c.priority ? ' active' : ''}`} title="Toggle priority" onClick={(ev) => togglePriority(c.candidate_id, c.priority, ev)}>{c.priority ? '★' : '☆'}</button>
                  <div className="mi-avatar">{initials(c.candidate_name)}</div>
                  <div className="mi-thread-meta">
                    <div className="mi-thread-top-row">
                      <span className="mi-thread-name">{c.candidate_name}</span>
                      <span className="mi-thread-time">{c.last_missed && (!c.last_message_at || new Date(c.last_missed) > new Date(c.last_message_at)) ? formatClock(c.last_missed) : c.last_message_at ? formatClock(c.last_message_at) : ''}</span>
                    </div>
                    {c.last_missed ? (
                      <div className="mi-thread-preview mi-thread-preview-missed">
                        <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                        {missedLabel(c)}
                      </div>
                    ) : c.last_message_failed ? (
                      <div className="mi-thread-preview mi-thread-preview-failed">⚠ Failed to send</div>
                    ) : (
                      <div className="mi-thread-preview">{(c.last_message || '').replace(/\n/g, ' ').slice(0, 54)}{(c.last_message || '').length > 54 ? '…' : ''}</div>
                    )}
                    <div className="mi-chip-row">
                      {c.source_type === 'prior' && <span className="mi-prior-chip">Prior — {c.company_name || 'Unknown'}</span>}
                      {trig && <span className={`mi-trigger-chip ${trig.cls}`}>{trig.label}</span>}
                      {c.nudge_reason && <span className="mi-nudge-chip">🔔 Nudge</span>}
                      {isAdmin && <span className="mi-recruiter-chip">{c.recruiter_name}</span>}
                    </div>
                  </div>
                  <div className="mi-row-actions">
                    {c.unread && <span className="mi-unread-dot"></span>}
                    <button className="mi-mark-unread-btn" title={c.unread ? 'Mark as read' : 'Mark as unread'} onClick={(ev) => toggleUnread(c.candidate_id, c.unread, ev)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
                    </button>
                  </div>
                </div>
              );
            })}

            {unmatched.length > 0 && (
              <div className="mi-unmatched-section">
                <div className="mi-unmatched-heading">Unmatched ({unmatched.length})</div>
                {unmatched.map(u => (
                  <div key={u.phone_number + u.recruiter_id} className="mi-unmatched-item">
                    <div className="mi-unmatched-info">
                      <div className="mi-unmatched-phone">{u.phone_number}</div>
                      <div className="mi-unmatched-preview">{(u.last_message || '').slice(0, 50)}</div>
                    </div>
                    <div className="mi-unmatched-actions">
                      <button onClick={() => { setPicker({ mode: 'attach', phoneNumber: u.phone_number, recruiterId: u.recruiter_id }); setPickerQuery(''); }}>Attach</button>
                      <button onClick={() => ignoreUnmatched(u)}>Ignore</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mi-thread-panel">
          {!thread && !threadLoading && (
            <div className="mi-empty-state">Select a conversation, or click "+ New" to text a candidate.</div>
          )}
          {threadLoading && <div className="mi-empty-state">Loading…</div>}
          {thread && !threadLoading && (
            <>
              <div className="mi-thread-header">
                <div className="mi-avatar mi-avatar-lg">{initials(thread.candidate.candidate_name)}</div>
                <div className="mi-thread-header-info">
                  <h3 className="mi-thread-title-row">{thread.candidate.candidate_name} <ClockButton onClick={() => setTimelineId(thread.candidate.id)} /></h3>
                  <div className="mi-sub">{thread.candidate.phone} &nbsp;·&nbsp; {thread.candidate.company_name || 'No company on file'}</div>
                </div>
                <div className="mi-spacer"></div>
                {thread.candidate.source_type === 'prior' && <span className="mi-context-pill mi-prior-pill">Prior — {thread.candidate.company_name || 'Unknown'}</span>}
                {thread.candidate.mountain_rating != null && <span className="mi-context-pill rating">Rated {thread.candidate.mountain_rating}/10</span>}
                <span className="mi-context-pill">Recruiter: {thread.candidate.recruiter_name || 'Unassigned'}</span>
                <div className="mi-header-actions">
                  {thread.candidate.phone && (
                    <button className="mi-call-btn" onClick={() => callCandidate(thread.candidate)}>
                      <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                      Call
                    </button>
                  )}
                  <button className={`mi-header-btn${thread.state.priority ? ' on-star' : ''}`} onClick={(ev) => togglePriority(thread.candidate.id, thread.state.priority, ev)}>
                    {thread.state.priority ? '★' : '☆'} Priority
                  </button>
                  <button className={`mi-header-btn${thread.state.nudge_reason ? ' on-nudge' : ''}`} onClick={openNudgePanel}>
                    🔔 {thread.state.nudge_reason ? nudgeLabel(thread.state) : 'Flag for Nudge'}
                  </button>
                  <button className="mi-header-btn" onClick={(ev) => toggleUnread(thread.candidate.id, thread.state.unread, ev)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
                    {thread.state.unread ? 'Mark as Read' : 'Mark as Unread'}
                  </button>
                </div>
              </div>

              {nudgePanelOpen && (
                <div className="mi-nudge-panel">
                  <label>Reason for nudge</label>
                  <div className="mi-nudge-panel-row">
                    <select value={nudgeReason} onChange={e => setNudgeReason(e.target.value)}>
                      {NUDGE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {nudgeReason === CUSTOM_REASON && (
                    <div className="mi-nudge-panel-row">
                      <input type="text" placeholder="Describe the reason…" value={nudgeCustom} onChange={e => setNudgeCustom(e.target.value)} style={{ flex: 1 }} />
                    </div>
                  )}
                  <div className="mi-nudge-panel-actions">
                    {thread.state.nudge_reason && <button className="mi-nudge-clear-btn" onClick={clearNudge}>Clear Flag</button>}
                    <button className="mi-nudge-cancel-btn" onClick={() => setNudgePanelOpen(false)}>Cancel</button>
                    <button className="mi-nudge-save-btn" onClick={saveNudge}>Save Nudge</button>
                  </div>
                </div>
              )}

              <div className="mi-messages">
                {thread.missed && (
                  <div className="mi-missed-banner">
                    <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                    <span>{missedLabel(thread.missed)}. Call or text back to clear it.</span>
                    {thread.candidate.phone && <button className="mi-call-btn" onClick={() => callCandidate(thread.candidate)}>Call back</button>}
                  </div>
                )}
                {thread.messages.length === 0 && !thread.missed && (
                  <div className="mi-date-sep">No messages yet — send the first one below</div>
                )}
                {(() => { lastDay = null; return null; })()}
                {thread.messages.map(m => {
                  const label = dayLabel(m.created_at);
                  const showSep = label !== lastDay;
                  lastDay = label;
                  return (
                    <React.Fragment key={m.id}>
                      {showSep && <div className="mi-date-sep">{label}</div>}
                      <div className={`mi-bubble-row ${m.direction === 'outbound' ? 'out' : 'in'}`}>
                        <div className="mi-bubble-col">
                          <div className={`mi-bubble${m.send_failed ? ' mi-bubble-failed' : ''}`}>{m.body.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}</div>
                          {m.send_failed ? (
                            <div className="mi-send-failed-note">⚠ Sorry, we're having trouble sending this message. {m.fail_reason ? <span title={m.fail_reason}>Details</span> : null}</div>
                          ) : (
                            <div className="mi-bubble-meta">{m.auto_sent ? <span className="mi-auto-tag">⚡ Auto-sent</span> : null}{m.auto_sent ? ' · ' : ''}{m.original_body ? <><span className="tcn-corrected" title={`You typed: ${m.original_body}`}>✓ Corrected</span> · </> : null}{formatClock(m.created_at)}</div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="mi-composer">
                <div className="mi-composer-hint">
                  <span className="mi-dot"></span> Sending as <strong>{thread.candidate.recruiter_name || 'you'}</strong>{thread.fromNumber ? <> from <span>{thread.fromNumber}</span></> : null}
                  {!thread.candidate.recruiter_connected && <span className="mi-warn"> — RingCentral not connected for this recruiter</span>}
                </div>
                <TextCheckNotice question={clarifyQ || draftUnclear} sending={sending} onSendAsWritten={() => sendReply(true)} />
                <div className="mi-composer-box">
                  <textarea rows={1} placeholder="Type a reply…" value={replyText}
                    onChange={e => { setReplyText(e.target.value); setClarifyQ(null); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} />
                  <button className="mi-send-btn" disabled={sending || !replyText.trim()} onClick={() => sendReply()}>{sending ? '…' : 'Send'}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {picker && (
        <div className="mi-picker-overlay" onClick={() => setPicker(null)}>
          <div className="mi-picker-modal" onClick={e => e.stopPropagation()}>
            <h3>{picker.mode === 'attach' ? `Attach ${picker.phoneNumber} to a candidate` : 'New Message'}</h3>
            <input autoFocus type="text" placeholder="Search candidates by name…" value={pickerQuery} onChange={e => setPickerQuery(e.target.value)} />
            <div className="mi-picker-results">
              {pickerResults.map(c => (
                <div key={c.id} className="mi-picker-result" onClick={() => choosePickerCandidate(c)}>
                  <div className="mi-avatar">{initials(c.candidate_name)}</div>
                  <div>
                    <div className="mi-picker-result-name">{c.candidate_name}</div>
                    <div className="mi-picker-result-sub">{c.phone} · {c.company_name || 'No company'}{!c.recruiter_connected ? ' · RC not connected' : ''}</div>
                  </div>
                </div>
              ))}
              {pickerQuery && pickerResults.length === 0 && <div className="mi-picker-empty">No matching candidates</div>}
            </div>
            <button className="mi-picker-close" onClick={() => setPicker(null)}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div className="mi-error-toast" onClick={() => setError('')}>{error}</div>}
      {timelineId && <TimelineDrawer candidateId={timelineId} user={user} onClose={() => setTimelineId(null)} />}
    </div>
  );
}
